/* @ngInject */
function assetPreviewService (
  $compile,
  $controller,
  $timeout,
  $rootScope,
  $cookies,
  ModalStackService,
  CONSTANTS,
  Utils,
  ConnectorWebService,
  SegmentService
) {
  const getAssetPreviewDialogController = import('../dialogs/asset-preview-dialog/asset-preview-dialog.controller')
  const getAssetQuickPreviewDialogController = import('../dialogs/asset-quick-preview-dialog/asset-quick-preview-dialog.controller')

  /*******************************
        Utility methods
*******************************/

  const api = {
    _openPreview,
    _prepareDialogElement,
    _injectAndGetModal,
    _appendModal,
    _toKebabCase
  }

  return {
    getAssetPreview,
    getArtPreview,
    getArtThumbnail,
    openAssetPreview,
    getAssetThumbnail,
    openAssetQuickPreview,
    closePreview,
    assetPreviewIsOpen,
    assetQuickPreviewIsOpen,
    isPreviewEnabled,
    updatePreviewScope,
    getAssetInsights,
    getCustomizableAssetInsights,
    exportAssetInsights,
    getModuleForLocation
  }

  /**
   * Opens preview depending on params.
   *
   * @param {Object} params - Preview parameter.
   * @param {Function} params.controller - Preview's controller.
   * @param {string} params.previewName - Preview's name.
   * @param {Object} params.data - Data to be injected into preview's scope.
   */
  function _openPreview (params) {
    const { modalEl, modalScope } = api._injectAndGetModal(
      {
        controller: params.controller,
        previewName: api._toKebabCase(params.previewName),
        dialogData: params.data || {}
      },
      {
        $rootScope,
        $compile,
        $controller
      }
    )

    ModalStackService.registerOpenedModal(
      {
        modalEl,
        modalScope,
        type: params.previewType
      },
      closePreview
    )
  }

  /**
   * Creates preview object and injects into DOM.
   *
   * @param {Object} params - Preview parameter.
   * @param {Function} params.controller - Preview's controller.
   * @param {string} params.previewName - Preview's name.
   * @param {Object} params.data - Data to be injected into preview's scope.
   * @param {Object} angularProviders - Object of Angular providers to be injected into preview scope.
   * @returns {Object} { modalEl, modalScope }
   */
  function _injectAndGetModal (params, angularProviders) {
    const { previewName, controller, dialogData } = params

    const { modalEl, modalScope } = api._prepareDialogElement(previewName, controller, dialogData, angularProviders)
    api._appendModal(modalEl[0])

    return {
      modalEl: modalEl[0],
      modalScope
    }
  }

  /**
   * Creates preview object.
   *
   * @param {Object} previewName - Preview's name.
   * @param {Function} controller - Preview's controller.
   * @param {string} dialogData - Data to be injected into preview's scope.
   * @param {Object} angularProviders - Object of Angular providers to be injected into preview scope.
   * @returns {Object} { modalEl, modalScope, modalController }
   */
  function _prepareDialogElement (previewName, controller, dialogData, angularProviders) {
    const { $rootScope, $compile, $controller } = angularProviders

    const dialogEl = document.createElement(previewName)
    const angularEl = angular.element(dialogEl)

    // compile elements in order to angular data binding / scope work properly
    const linkFn = $compile(angularEl)
    const modalScope = $rootScope.$new()

    // inject modal data inside scope
    modalScope.data = dialogData

    let modalController

    if (typeof controller === 'function') {
      modalController = $controller(controller, { $scope: modalScope })
    }

    return {
      modalEl: linkFn(modalScope),
      modalScope,
      modalController
    }
  }

  /**
   * Injects preview element into DOM.
   *
   * @param {Object} modalEl - Preview's element.
   */
  function _appendModal (modalEl) {
    const body = document.querySelector('body')
    body.append(modalEl)
  }

  /**
   * Transforms Camel case into Kebab case
   *
   * @param {String} previewName - Preview's name.
   * @returns {String} - Preview's name into Kebab case
   */
  function _toKebabCase (previewName) {
    return previewName
      .replace(/([a-z])([A-Z])/g, '$1-$2')
      .replace(/[\s_]+/g, '-')
      .toLowerCase()
  }

  /*******************************
        Exported methods
*******************************/

  /**
   * Returns the asset preview URL
   *
   * @param {Number} assetId - Asset ID.
   */
  function getAssetPreview (assetId, moduleLocation, options = {}) {
    let url = Utils.fnReplaceStringParams(CONSTANTS.URL_ASSET_PREVIEW, [assetId])

    // by default we add the jwt token in the URL
    if (!options.noJwt) {
      url = Utils.fnAddQueryToUrl(url, { jwt: $cookies.get('jwt') })
    }

    if (moduleLocation) {
      url = Utils.fnAddQueryToUrl(url, { previewModule: moduleLocation })
    }

    if (options.forceRefresh) {
      url = Utils.fnAddQueryToUrl(url, { dateCache: new Date().getTime() })
    }

    if (options.version) {
      url = Utils.fnAddQueryToUrl(url, { assetVersion: options.version })
    }

    if (options.quality) {
      url = Utils.fnAddQueryToUrl(url, { quality: options.quality })
    }

    return url
  }

  /**
   * Returns the art preview URL
   *
   * @param {Number} artId - Art ID.
  */
  function getArtPreview (artId, layerId, options) {
    const artUrl = Utils.fnReplaceStringParams(CONSTANTS.URL_ART_PREVIEW, [artId])
    let url = Utils.fnAddQueryToUrl(artUrl, { jwt: $cookies.get('jwt'), artLayerId: layerId })

    if (options?.quality) {
      url = Utils.fnAddQueryToUrl(url, { quality: options.quality })
    }

    return url
  }

  /**
   * Returns the art thumbnail URL
   *
   * @param {Number} artId - Art ID.
   */
  function getArtThumbnail (artId, layerId) {
    const artUrl = Utils.fnReplaceStringParams(CONSTANTS.URL_ARTS_THUMBNAIL_ID, [artId])
    const url = Utils.fnAddQueryToUrl(artUrl, { artLayerId: layerId })

    return url
  }

  function getAssetThumbnail (assetId, layerId) {
    const assetUrl = Utils.fnReplaceStringParams(CONSTANTS.URL_CUSTOMER_ASSETS_THUMBNAIL_ID, [assetId])
    const url = Utils.fnAddQueryToUrl(assetUrl, { artLayerId: layerId })

    return url
  }

  /**
   * Closes the current preview
   */
  function closePreview () {
    const lastModalOpened = ModalStackService.getLastModalOpened()
    if (lastModalOpened?.type !== 'asset-preview' && lastModalOpened?.type !== 'quick-preview') {
      return
    }

    ModalStackService.closeLastOpenedModal()
    const angularEl = angular.element(lastModalOpened?.modalEl)
    angularEl.scope().$destroy()
    angularEl.remove()
    jQuery(lastModalOpened.modalEl).remove()
  }

  /**
   * Opens preview dialog.
   *
   * @param {Object} dialogData - Data to be injected into preview's scope.
   * @param {string} dialogData.openTab - Tab name that you want to be opened when the preview is showed
   */
  function openAssetPreview (dialogData) {
    const isMaterialModule = getModuleForLocation(dialogData.location) === 'MATERIAL'
    if (isMaterialModule) {
      SegmentService.track('arts-preview-open', {
        type: dialogData.asset.isCustomizable ? 'customizable' : 'upload',
        asset_id: dialogData.asset.id,
        place: dialogData.location
      })
    }

    getAssetPreviewDialogController.then(({ AssetPreviewDialogController }) => {
      api._openPreview({
        controller: AssetPreviewDialogController,
        previewName: 'assetPreviewDialog',
        data: dialogData,
        previewType: 'asset-preview'
      })
    })
  }

  /**
   * Opens quick preview dialog.
   *
   * @param {Object} dialogData - Data to be injected into preview's scope.
   */
  function openAssetQuickPreview (dialogData) {
    SegmentService.track('arts-preview-quick-open', {
      type: dialogData.currentAsset.isCustomizable ? 'customizable' : 'upload'
    })

    getAssetQuickPreviewDialogController.then(({ AssetQuickPreviewDialogController }) => {
      api._openPreview({
        controller: AssetQuickPreviewDialogController,
        previewName: 'assetQuickPreviewDialog',
        data: dialogData,
        previewType: 'quick-preview'
      })
    })
  }

  function updatePreviewScope (properties = {}) {
    const openedModals = ModalStackService.getOpenedModals()

    openedModals.forEach(modal => {
      if (modal.type !== 'asset-preview' && modal.type !== 'quick-preview') {
        return
      }

      const scope = modal.modalScope
      $timeout(() => {
        Object.keys(properties).forEach(key => {
          scope[key] = properties[key]
        })
      })
    })
  }

  function assetPreviewIsOpen () {
    const openedModals = ModalStackService.getOpenedModals()
    return openedModals.some(modal => modal.type === 'asset-preview')
  }

  function assetQuickPreviewIsOpen () {
    const openedModals = ModalStackService.getOpenedModals()
    return openedModals.some(modal => modal.type === 'quick-preview')
  }

  function isPreviewEnabled () {
    return true
  }

  function getAssetInsights (assetId, start, end) {
    let url = Utils.fnReplaceStringParams(CONSTANTS.URL_ANALYTICS_INSIGHTS_PREVIEW_ASSET)
    url = Utils.fnAddStartEndQueriesToUrl(url, { start, end })
    url = Utils.fnAddQueryToUrl(url, { artId: assetId })

    return ConnectorWebService.get(url)
  }

  function getCustomizableAssetInsights (artId, start, end, needApproval) {
    const urlConstant = needApproval
      ? CONSTANTS.URL_ANALYTICS_INSIGHTS_PREVIEW_CUSTOMIZABLE_APPROVAL_ASSET
      : CONSTANTS.URL_ANALYTICS_INSIGHTS_PREVIEW_CUSTOMIZABLE_ASSET

    let url = Utils.fnReplaceStringParams(urlConstant)

    url = Utils.fnAddStartEndQueriesToUrl(url, { start, end })
    url = Utils.fnAddQueryToUrl(url, { artId })

    return ConnectorWebService.get(url)
  }

  function exportAssetInsights (slug, isCustomizable, id, start, end) {
    let url = Utils.fnReplaceStringParams(CONSTANTS.URL_ANALYTICS_INSIGHTS_PREVIEW_EXPORT, [slug])

    url = Utils.fnAddStartEndQueriesToUrl(url, { start, end })

    if (isCustomizable) {
      url = Utils.fnAddQueryToUrl(url, { artId: id })
    } else {
      url = Utils.fnAddQueryToUrl(url, { assetId: id })
    }

    return ConnectorWebService.get(url)
  }

  function getModuleForLocation (location) {
    switch (location) {
      case 'workflow':
        return 'WORKFLOW'
      case 'materials':
      case 'materialsCustomizations':
      case 'materialsApproval':
        return 'MATERIAL'
      case 'posts':
        return 'POSTS'
      case 'brand':
        return 'BRAND'
      case 'checklist':
        return 'CHECKLIST'
      case 'repository':
        return 'REPOSITORY'
    }
  }
}

export { assetPreviewService }
